/** スケジュール詳細コンポーネント */
<template>
  <div class="schedule-detail">
    <ifield-b-modal
      v-bind:id="modalId"
      ref="the-schedule-detail"
      v-bind:title="$t('theScheduleDetail.scheduleDetail')"
      size="lg"
      centered
      scrollable
      no-body-border
      body-class="schedule-detail-dialog-body"
      v-on:hidden="$_clearData"
    >
      <!-- 対象ユーザー -->
      <b-row no-gutters class="user">
        <b-col>
          <p>
            {{ displayUserName }}
          </p>
        </b-col>
      </b-row>
      <!-- スケジュール概要 -->
      <schedule-summary
        v-bind:schedule="schedule"
        v-bind:main-user-id="mainUserId"
        v-bind:showAssignedUserChangeLabel="!isCompletedTask"
        v-on:change-assign-user-click="$_showAssignUserEditor"
      />
      <!-- スケジュール詳細 -->
      <b-row no-gutters class="detail">
        <p class="schedule-detail" v-html="displayDetail" />
      </b-row>
      <!-- タスク(チェックイン) -->
      <b-row v-if="isCheckinAvailable" no-gutters class="task">
        <!-- タイトル -->
        <b-col cols="4" class="task-title-col">
          {{ $t('theScheduleDetail.title.place') }}
        </b-col>
        <!-- 内容 -->
        <b-col class="task-detail-col checkin-detail-col">
          <template v-if="schedule.checkinId">
            <span
              class="place-name checkin-done"
              v-on:click="$_showCheckinDetail"
            >
              {{ placeName }}
            </span>
          </template>
          <template v-else>
            <span class="place-name">{{ placeName }}</span>
          </template>
          <span class="place-address">{{ placeAddress }}</span>
        </b-col>
      </b-row>
      <!-- タスク(報告書) -->
      <b-row v-if="isReportAvailable" no-gutters class="task">
        <!-- タイトル -->
        <b-col cols="4" class="task-title-col">
          {{ $t('theScheduleDetail.title.reportProject') }}
        </b-col>
        <!-- 内容 -->
        <b-col class="task-detail-col report-detail-col">
          <!-- 報告書IDがある時 -->
          <template v-if="schedule.reportId">
            <div class="report-detail">
              <span class="report-title" v-on:click="$_showReportDetail">
                {{ reportTitle }}
              </span>
              <span class="report-attach-files">
                <img
                  class="attach-file-icon"
                  src="@/assets/ifield/icon_attach_file.png"
                />
                {{ reportAttachFilesCount }}
              </span>
            </div>
          </template>
          <!-- 報告書IDがない時 -->
          <template v-else-if="isReportCreatable">
            <span class="report-project-name">
              {{ reportProjectName }}
            </span>
            <span
              v-bind:class="[
                'report-project-name',
                isCompletedTask ? 'completed' : 'editable',
              ]"
              v-on:click="$_showReportEditor"
            >
              {{ $t('theScheduleDetail.createReport') }}
            </span>
          </template>
          <template v-else>
            <span class="report-project-name">{{ reportProjectName }}</span>
          </template>
        </b-col>
      </b-row>
      <!-- スケジュール属性 -->
      <b-row
        v-for="attr in formattedAttributes"
        v-bind:key="attr.attribute_id"
        v-bind:class="{
          'label-attribute-row': attr.attribute_type == ATTRIBUTE_TYPE.LABEL,
        }"
        no-gutters
        class="schedule-attribute"
      >
        <!-- タイトル -->
        <b-col
          v-if="attr.attribute_type == ATTRIBUTE_TYPE.LABEL"
          class="schedule-attribute-label"
        >
          {{ attr.attribute_name }}
        </b-col>
        <b-col v-else cols="4" class="schedule-attribute-title-col">
          <div class="attribute-name">{{ attr.attribute_name }}</div>
        </b-col>

        <!-- 内容 -->
        <b-col
          v-if="attr.attribute_type != ATTRIBUTE_TYPE.LABEL"
          class="schedule-attribute-contents-col"
        >
          <div v-if="attr.attribute_type == ATTRIBUTE_TYPE.STRING">
            <span
              v-for="(text, index) in attr.attribute_value"
              v-bind:key="`${attr.attribute_id}-${index}`"
              class="each-line"
            >
              <!-- 空行の場合高さがなくなるので&nbsp;を挿入 -->
              {{ text.length ? text : '&nbsp;' }}
            </span>
          </div>

          <div v-else-if="attr.attribute_type == ATTRIBUTE_TYPE.LINK">
            <a
              v-bind:href="formatURL(attr.attribute_value)"
              target="_blank"
              class="link-text"
            >
              {{ attr.attribute_value }}
            </a>
          </div>

          <span v-else span>{{ attr.attribute_value }}</span>
        </b-col>
      </b-row>
      <!-- 添付ファイル -->
      <b-row v-if="hasScheduleAttachFile" no-gutters class="attach-file">
        <!-- タイトル -->
        <b-col cols="4" class="attach-file-title-col">
          {{ $t('theScheduleDetail.title.attachFile') }}
        </b-col>
        <!-- 内容 -->
        <b-col class="task-detail-col attach-file-col">
          <attach-file-viewer
            v-bind:attachFiles="scheduleAttachFiles"
            v-bind:recordId="scheduleId"
          />
        </b-col>
      </b-row>
      <!-- チェックイン詳細 -->
      <checkin-detail ref="checkin-detail" />
      <!-- 報告書詳細 -->
      <report-detail
        ref="report-detail"
        v-on:report-detail-editend="$_onReportEditEnd"
      />
      <!-- 報告書編集モーダル -->
      <report-editor
        ref="report-editor"
        v-on:report-editend="$_onReportEditEnd"
        v-on:report-editcancel="$_onReportEditCancel"
      />
      <!-- スケジュール編集フォーム -->
      <schedule-editor
        ref="schedule-editor"
        v-on:schedule-editend="$_onScheduleEditEnd"
        v-on:schedule-editfailed="$_onScheduleEditFailed"
      />
      <!-- 担当者選択モーダル -->
      <schedule-assign-user-editor
        ref="schedule-assign-user-editor"
        v-bind:schedule="schedule"
        v-on:assign-user-editend="$_onScheduleEditEnd"
        v-on:assign-user-editfailed="$_onScheduleEditFailed"
      />

      <!-- フッター -->
      <template v-slot:modal-footer="{ cancel }">
        <div class="schedule-modal-footer">
          <div class="schedule-modal-footer-left">
            <!-- 削除 -->
            <b-button
              v-if="isEditable"
              size="sm"
              variant="danger"
              v-t="'theScheduleDetail.action.delete'"
              v-on:click="$_deleteSchedule"
            />
          </div>
          <div class="schedule-modal-footer-right">
            <!-- 編集 -->
            <b-button
              v-if="isEditable"
              size="sm"
              variant="success"
              v-t="'theScheduleDetail.action.edit'"
              v-on:click="$_showEditor"
            />
            <!-- 閉じる -->
            <b-button
              size="sm"
              v-t="'theScheduleDetail.action.close'"
              v-on:click="cancel"
            />
          </div>
        </div>
      </template>
    </ifield-b-modal>
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
  </div>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { useAttributeFormatter } from 'ifield-core-components'
import { FUNCTION_TYPE } from '@/constants.js'
import Schedule from '@/model/Schedule.js'
import StringUtil from '@/js/util/StringUtil'
import ScheduleSummary from '@/components/schedule/AppScheduleSummary.vue'
import CheckinDetail from '@/views/TheCheckinDetail.vue'
import ReportDetail from '@/views/TheReportDetail.vue'
import ReportEditor from '@/views/TheReportEditor.vue'
import ScheduleEditor from '@/views/TheScheduleEditor.vue'
import { toLocalTz, toLocalTzAttribute } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import AttachFileViewer from '@/components/AppAttachFileViewer.vue'
import ScheduleAssignUserEditor from '@/views/TheScheduleAssignUserEditor.vue'

export default {
  name: 'TheScheduleDetail',
  components: {
    'checkin-detail': CheckinDetail,
    'report-detail': ReportDetail,
    'report-editor': ReportEditor,
    'schedule-summary': ScheduleSummary,
    'schedule-editor': ScheduleEditor,
    'attach-file-viewer': AttachFileViewer,
    'schedule-assign-user-editor': ScheduleAssignUserEditor,
  },
  props: {
    /**
     * モーダルのIDプレフィックス
     */
    idPrefix: {
      type: String,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    const { attributeFormatter, splitByLineFeed, formatURL } =
      useAttributeFormatter()
    return { handleApiError, attributeFormatter, splitByLineFeed, formatURL }
  },
  data: function () {
    return {
      // スケジュールデータ
      schedule: new Schedule(null, this.$log),
      // 対象ユーザー
      user: null,
      // プレイスデータ
      place: null,
      // 報告書データ
      report: null,
      // 報告書プロジェクト
      reportProject: null,
      // ローディング表示中の場合はtrue
      isLoading: false,
    }
  },
  computed: {
    /**
     * カスタム属性タイプ定数
     * @type {Object}
     */
    ATTRIBUTE_TYPE: function () {
      return ATTRIBUTE_TYPE
    },
    /**
     * ログインユーザーのID
     * @type {Number}
     */
    myUserId: function () {
      return this.$store.getters.loginState.userId
    },
    /**
     * メインとなるユーザーのID
     * @type {Number}
     */
    mainUserId: function () {
      if (this.user && this.user.id !== this.myUserId) {
        return this.user.id
      } else {
        return null
      }
    },
    /**
     * スケジュールID
     * @type {Number}
     */
    scheduleId: function () {
      return this.schedule && this.schedule.id
    },
    /**
     * チェックイン機能が利用可能な場合はtrue
     * @type {Boolean}
     */
    isCheckinAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.CHECKIN)
    },
    /**
     * 報告書機能が利用可能な場合はtrue
     * @type {Boolean}
     */
    isReportAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.REPORT)
    },
    /**
     * 表示ユーザー名
     * @type {String}
     */
    displayUserName: function () {
      // ユーザーが指定されていない時はログインユーザー
      const userName = this.user
        ? this.user.name
        : this.$store.getters.loginState.userName
      return this.$t('theScheduleDetail.onesSchedule', { name: userName })
    },
    /**
     * 表示用詳細
     * @type {String}
     */
    displayDetail: function () {
      if (!this.schedule.detail) return ''
      // v-html で出力するため、各行を HTML エスケープしてから改行を <br /> に変換（XSS対策）
      return this.schedule.detail
        .split('\n')
        .map((line) => StringUtil.escapeHtml(line))
        .join('<br />')
    },
    /**
     * プレイスの名称
     * @type {String}
     */
    placeName: function () {
      return this.schedule.placeName
    },
    /**
     * プレイスの住所
     * @type {String}
     */
    placeAddress: function () {
      if (!this.place) return ''
      return `${this.place.address1 || ''} ${this.place.address2 || ''}`
    },
    /**
     * 報告書タイトル
     * @type {String}
     */
    reportTitle: function () {
      if (!this.report) return this.$t('theScheduleDetail.invalidReport')
      return this.report.title
    },
    /**
     * 報告書の添付ファイル数
     * @type {Number}
     */
    reportAttachFilesCount: function () {
      if (!this.report) return 0
      if (!this.report.report_attach_files) return 0
      return this.report.report_attach_files.length
    },
    /**
     * 報告書プロジェクト名
     * @type {String}
     */
    reportProjectName: function () {
      return this.schedule.reportProjectName
    },
    /**
     * 報告書を作成可能な場合はtrue
     * @type {Boolean}
     */
    isReportCreatable: function () {
      return (
        !this.schedule.isReportCompleted &&
        this.schedule.isTargetUser(this.myUserId) &&
        this.reportProject != null
      )
    },
    /**
     * スケジュールの担当が自分だけの場合はtrue
     * @type {Boolean}
     */
    isOnlyMine: function () {
      return this.schedule.isTargetUserOnly(this.myUserId)
    },
    /**
     * スケジュール属性(値フォーマット済み)
     * @type {Array.<Object>}
     */
    formattedAttributes: function () {
      return (this.schedule.attributes || []).map(
        this.$_formatScheduleAttribute
      )
    },
    /**
     * スケジュール添付ファイルがある場合はtrue
     * @type {Boolean}
     */
    hasScheduleAttachFile: function () {
      return !!this.schedule.scheduleAttachFiles.length
    },
    /**
     * 添付ファイル
     * @type {Array.<Object>}
     */
    scheduleAttachFiles: function () {
      return this.schedule && this.schedule.scheduleAttachFiles
    },
    /**
     * スケジュールを編集・削除できる場合はtrue
     * @type {Boolean}
     */
    isEditable: function () {
      return this.$store.state.login.isManager || this.isOnlyMine
    },
    /**
     * モーダルのID
     * @type {String}
     */
    modalId: function () {
      return this.idPrefix
        ? `${this.idPrefix}-the-schedule-detail`
        : 'the-schedule-detail'
    },
    /**
     * スケジュール完了フラグ
     * @type {Boolean}
     */
    isCompletedTask: function () {
      return this.schedule.completeFlg
    },
  },
  mounted: function () {
    this.$log.info('TheScheduleDetail mounted.')
  },
  methods: {
    /**
     * モーダルを表示する
     *
     * @param {Number} id スケジュールID
     * @param {Object} user 対象ユーザー
     * @public
     */
    showModal: async function (id, user) {
      this.$log.debug('show schedule detail id:', id, 'user:', user)
      if (user) {
        this.user = user
      }

      // スケジュール表示
      this.$_showLoading()
      try {
        await this.$_showSchedule(id)
        this.$bvModal.show(this.modalId)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * モーダルを消す
     * @public
     */
    hideModal: function () {
      this.$bvModal.hide(this.modalId)
    },

    /**
     * スケジュール属性の値をフォーマット
     * @param {Object} data 整形対象データ
     * @returns {Object} 整形済み値
     */
    $_formatScheduleAttribute: function (data) {
      const dataModified = { ...data }
      const value = data.attribute_value
      const type = data.attribute_type

      if (type == ATTRIBUTE_TYPE.STRING) {
        dataModified.attribute_value = this.splitByLineFeed(value)
      } else if (type in this.attributeFormatter) {
        dataModified.attribute_value = this.attributeFormatter[type](value)
      }
      return dataModified
    },

    /**
     * スケジュールを表示する
     *
     * @param {Number} id スケジュールID
     */
    $_showSchedule: async function (id) {
      // スケジュール取得
      await this.$_getSchedule(id)

      // タスク詳細(プレイス・報告書)取得
      let placePromise = null
      if (!this.schedule.checkinTask) {
        this.place = null
        placePromise = Promise.resolve(null)
      } else {
        placePromise = this.$_getPlace()
      }
      let reportPromise = null
      if (!this.schedule.reportTask) {
        this.report = null
        this.reportProject = null
        reportPromise = Promise.resolve(null)
      } else if (this.schedule.reportId) {
        reportPromise = this.$_getReport()
      } else {
        reportPromise = this.$_getReportProject()
      }
      await Promise.all([placePromise, reportPromise])
    },

    /**
     * スケジュールを取得する
     *
     * @param {Nunber} id スケジュールID
     */
    $_getSchedule: async function (id) {
      const url = RestApiClient.createApiUrl(`schedule/${+id}`)

      let d
      try {
        d = await RestApiClient.executeGetApi(url).then(({ data }) => data)
      } catch (error) {
        this.handleApiError(error)
        throw error
      }

      const record = {
        ...d,
        start_at: d.allday_flg ? d.start_at : toLocalTz(d.start_at),
        end_at: d.allday_flg ? d.end_at : toLocalTz(d.end_at),
        create_at: toLocalTz(d.create_at),
        update_at: toLocalTz(d.update_at),
        attributes: d.attributes.map((attr) => toLocalTzAttribute(attr)),
      }
      this.schedule = new Schedule(record, this.$log)
    },

    /**
     * プレイス詳細を取得する
     */
    $_getPlace: async function () {
      const placeId = this.schedule.checkinTask.parent_id
      const url = RestApiClient.createApiUrl(`place/${+placeId}`)
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.place = json.data
      } catch (error) {
        this.handleApiError(error, null)
      }
    },

    /**
     * 報告書詳細を取得する
     */
    $_getReport: async function () {
      const reportId = this.schedule.reportTask.id
      const url = RestApiClient.createApiUrl(`report/${+reportId}`)
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.report = json.data
      } catch (error) {
        this.handleApiError(error, null)
      }
    },

    /**
     * 報告書プロジェクトを取得する
     */
    $_getReportProject: async function () {
      const reportProjectId = this.schedule.reportTask.parent_id
      const url = RestApiClient.createApiUrl(
        `report_project/${+reportProjectId}`
      )
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.reportProject = json.data
      } catch (error) {
        this.handleApiError(error, null)
      }
    },

    /**
     * 担当者選択モーダル表示
     */
    $_showAssignUserEditor: function () {
      this.$refs['schedule-assign-user-editor'].showModal()
    },

    /**
     * チェックイン詳細を表示する
     */
    $_showCheckinDetail: function () {
      this.$refs['checkin-detail'].showModal(this.schedule.checkinTask.id)
    },

    /**
     * 報告書詳細を表示する
     */
    $_showReportDetail: function () {
      this.$refs['report-detail'].showModal(this.schedule.reportTask.id)
    },

    /**
     * 報告書編集フォームを表示する
     */
    $_showReportEditor: function () {
      if (!this.isCompletedTask) {
        this.$refs['report-editor'].showModal(null, {
          scheduleId: this.schedule.id,
          reportProjectId: this.schedule.reportTask.parent_id,
          userId: this.myUserId,
          placeId: this.schedule.checkinTask
            ? this.schedule.checkinTask.parent_id
            : null,
        })
      }
    },

    /**
     * スケジュールを削除する
     */
    $_deleteSchedule: async function () {
      const isAgreed = await this.$_confirmDeleteSchedule()

      if (isAgreed) {
        this.$_showLoading()

        const url = RestApiClient.createApiUrl(`schedule/${this.schedule.id}`)
        try {
          await RestApiClient.executeDeleteApi(url)
        } catch (error) {
          this.handleApiError(error)
          this.$_hideLoading()
          return
        }

        /**
         * スケジュール削除完了イベント
         */
        this.$emit('schedule-deleteend')
        this.$_showDeleteFinishedToast()
        this.hideModal()
        this.$_hideLoading()
      }
    },

    /**
     * スケジュールを削除してよいか確認する
     *
     * @returns {Promise.<Boolean>} 削除してよければtrue
     */
    $_confirmDeleteSchedule: function () {
      let confirmText = ''
      if (this.schedule.targetUsers.length === 1) {
        confirmText = this.$t('theScheduleDetail.message.confirmDelete')
      } else {
        const otherUserNames = this.schedule.targetUsers
          .filter((user) => user.user_id !== this.myUserId)
          .map((user) => user.user_name)
        confirmText = `${this.$t(
          'theScheduleDetail.message.confirmDeleteWithOtherUsers'
        )}<br/>${otherUserNames.join(',')}`
      }

      return this.$alert
        .fire({
          icon: 'warning',
          title: this.$t('theScheduleDetail.confirm'),
          html: confirmText,
          showCancelButton: true,
          confirmButtonText: this.$t('theScheduleDetail.action.ok'),
          cancelButtonText: this.$t('theScheduleDetail.action.cancel'),
        })
        .then((res) => {
          return !!res.value
        })
    },

    /**
     * 削除完了トーストを表示する
     */
    $_showDeleteFinishedToast: function () {
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: this.$t('theScheduleDetail.message.deleted'),
        confirmButtonText: this.$t('theScheduleDetail.action.ok'),
        confirmButtonColor: '#6c757d',
        timer: 2000,
      })
    },

    /**
     * スケジュール編集フォームを表示する
     */
    $_showEditor: function () {
      this.$refs['schedule-editor'].showModal(this.schedule.toJSON())
    },

    /**
     * 各種データをクリアする
     */
    $_clearData: function () {
      this.schedule = new Schedule(null, this.$log)
      this.user = null
      this.place = null
      this.report = null
      this.reportProject = null
    },

    /**
     * スケジュール再読み込み
     */
    $_reloadSchedule: async function () {
      this.$_showLoading()
      try {
        await this.$_showSchedule(this.schedule.id)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * スケジュール編集完了時の処理
     */
    $_onScheduleEditEnd: function () {
      this.$_emitEditEndEvent()
      this.$_reloadSchedule()
    },

    /**
     * スケジュール編集失敗時の処理
     * @param {Number} id 失敗したスケジュールID
     */
    $_onScheduleEditFailed: function (id) {
      this.$_emitEditFailedEvent(id)
      this.$_reloadSchedule()
    },

    /**
     * 報告書編集完了時処理
     */
    $_onReportEditEnd: async function () {
      this.$_emitEditEndEvent()
      this.$_reloadSchedule()
    },

    /**
     * 報告書編集キャンセル時処理
     */
    $_onReportEditCancel: function () {
      this.$_emitEditFailedEvent(this.schedule.id)
      this.hideModal()
    },

    /**
     * スケジュール保存完了イベント発行
     */
    $_emitEditEndEvent: function () {
      /**
       * スケジュール保存完了イベント
       * @param {Number} id スケジュールID
       */
      this.$emit('schedule-editend', this.schedule.id)
    },

    /**
     * スケジュール保存失敗イベント発行
     * @param {Number} id 失敗したスケジュールID
     */
    $_emitEditFailedEvent: function (id) {
      /**
       * スケジュール保存失敗イベント
       * @param {Number} id 失敗したスケジュールID
       */
      this.$emit('schedule-editfailed', id)
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
$-color-gray: #707070;
$-color-lightgray: #959595;

::v-deep .schedule-detail-dialog-body {
  padding: 0 20px 20px 20px;
}

.user {
  text-align: center;
  font-weight: bold;
  padding-top: 20px;
  position: sticky;
  top: 0;
  background-color: $dialog-background-color;
  z-index: 1;
}

.detail {
  padding: 20px;
  border-bottom: 1px solid $-color-gray;

  .schedule-detail {
    font-size: $font-size-middle;
    margin: 0;
  }
}

.task {
  padding: 20px 20px;
  border-bottom: 1px dashed $-color-gray;

  .task-title-col {
    font-size: $font-size-middle;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
}

.label-attribute-row {
  background-color: #e0e0e0;
  border-bottom: 1px dashed $-color-gray;
}

.schedule-attribute {
  padding: 15px 20px;
  border-bottom: 1px dashed $-color-gray;
  .schedule-attribute-label {
    font-size: $font-size-middle;
  }

  .schedule-attribute-title-col {
    font-size: $font-size-middle;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    .attribute-name {
      // 12文字で折り返す仕様
      width: calc($font-size-middle * 12);
    }
  }

  .schedule-attribute-contents-col {
    // 複数行の場合は行間を10px確保するので、値の行間を保ちつつ行内上下の余白20pxを取るため、
    // padding上下15px + 文字列上下の余白5pxとなるよう調整
    // (font-size: 16pxなので、(16px * 1.625 - 16) / 2 = 5px)
    line-height: 1.625;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    .each-line {
      display: block;
    }
  }

  .link-text {
    color: $text-color-link;
  }
}

.attach-file {
  padding: 20px 20px;
  border-bottom: 1px dashed $-color-gray;

  .attach-file-title-col {
    font-size: $font-size-middle;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    margin-top: 24px;
  }

  .attach-file-col {
    margin: 8px 8px 8px 0;
  }
}

.checkin-detail-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  .place-name {
    &.checkin-done {
      color: $text-color-link;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .place-address {
    font-size: $font-size-middle;
  }
}

.report-detail-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  .report-project-name {
    &.completed {
      font-size: $font-size-middle;
      color: $-color-lightgray;
      text-decoration: underline;
    }
    &.editable {
      font-size: $font-size-middle;
      color: $text-color-link;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .report-detail {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .report-title {
    color: $text-color-link;
    text-decoration: underline;
    margin-right: 0.8em;
    cursor: pointer;
  }

  .report-attach-files {
    font-size: 0.9em;
    color: $-color-gray;
    text-align: left;

    .attach-file-icon {
      width: 16px;
      height: 16px;
      position: relative;
      top: -1px;
      right: -1px;
    }
  }
}

.schedule-modal-footer {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .schedule-modal-footer-right {
    :not(:last-child) {
      margin-right: 10px;
    }
  }
}
</style>
